import { Badge, Card, CardHeader, ScreenHeader, cn } from '@/components/ui'
import { useRouter, type TabKey } from '@/routes'
import { stageLabel } from '@/screens/Case/stageLabels'

/**
 * The guide — what this platform does, what it will not do, and what stays
 * yours.
 *
 * Every other screen answers a question about one matter. This one answers the
 * questions somebody has before they trust the thing at all: what happens to a
 * document I paste, which parts are a machine's opinion, and what am I still on
 * the hook for. Those answers existed only in `docs/` — read by whoever builds
 * this, never by whoever uses it.
 *
 * ## It is written from the repository, not from marketing
 *
 * Every claim here is one this codebase can support. The stage list is
 * `RUN_ORDER`; the offices are `JURISDICTION_OFFICES`; the decline reasons are
 * `DeclineReason`; the drafting habits under "Read the draft for these" are the
 * two patterns `docs/HELP.md` found reviewing six real drafts line by line, and
 * the missing signature block is a real open defect rather than a hypothetical.
 * The limits section is `docs/HELP.md`'s own "What has not been tested,
 * honestly" — a guide that lists only what works is an advertisement, and an
 * attorney who discovers a limit after filing has been misled by it.
 *
 * Where a number would go stale the moment a corpus is reloaded, the text says
 * what kind of thing it is instead of how many there are.
 */

// ---------------------------------------------------------------------------
// The journey, end to end
// ---------------------------------------------------------------------------

interface JourneyStep {
  title: string
  detail: string
  yours: boolean
}

/**
 * Five steps, and who owns each.
 *
 * `yours` is the whole point of the diagram: two of the five are an attorney's
 * decision and no run proceeds past them on its own. Drawing them in the same
 * colour as the machine's steps would be the single most misleading thing this
 * page could do.
 */
const JOURNEY: readonly JourneyStep[] = [
  {
    title: 'Bring the document in',
    detail:
      'Paste the office action, upload the PDF, give a link to it, or look the application up on the register.',
    yours: false,
  },
  {
    title: 'Confirm what it says',
    detail:
      'The office, the grounds raised, the mark, the applicant. Nothing is computed until you agree these are right.',
    yours: true,
  },
  {
    title: 'Run it',
    detail:
      'Six steps, from reading the document to writing a draft. Roughly a second without drafting, about a minute with it.',
    yours: false,
  },
  {
    title: 'Confirm the deadline, then review',
    detail:
      'Sign off the response date against the papers, work through what the checks flagged, and read the draft properly.',
    yours: true,
  },
  {
    title: 'Sign and export',
    detail:
      'Signing re-checks the deadline; exporting re-checks everything else and produces the letter. You file it — this never does.',
    yours: true,
  },
]

// ---------------------------------------------------------------------------
// What happens inside one run
// ---------------------------------------------------------------------------

interface StageRow {
  /**
   * The pipeline stage this step is. Never shown: it is only how the step finds
   * the name the Case screen gives it, so the two cannot drift apart.
   */
  stage: string
  step: string
  plain: string
  stops: string | null
}

/** The runner's own order, said without its stage identifiers. */
const STAGES: readonly StageRow[] = [
  {
    stage: 's1_parse',
    step: 'Read the document',
    plain:
      'Checks there is real text to work with, and runs OCR when the PDF is only a picture of a page.',
    stops: 'Too little readable text — the matter is closed as unreadable.',
  },
  {
    stage: 's2_normalize',
    step: 'Work out the basics',
    plain:
      'Which office, which grounds were raised, and the response deadline — from the papers and from the office’s own rules.',
    stops: 'Not in English, or the two deadline dates disagree.',
  },
  {
    stage: 's3_analyze',
    step: 'Decide what must be answered',
    plain:
      'Turns the objection into the specific points a response has to meet, ground by ground.',
    stops: null,
  },
  {
    stage: 's4_research',
    step: 'Look up the law',
    plain:
      'Pulls provisions, decided cases and argument patterns for those grounds from the law library.',
    stops: null,
  },
  {
    stage: 's5_generate',
    step: 'Write the draft',
    plain:
      'The only step that calls a model, and the only one that costs anything. Skipped entirely on a checks-only run.',
    stops: null,
  },
  {
    stage: 's6_review',
    step: 'Check what came back',
    plain:
      'Deterministic rules, not a second opinion from a model: every citation resolved, every ground answered, no fact asserted that nobody supplied.',
    stops: 'Anything it refuses is listed for you, and export stays shut until it is cleared.',
  },
]

// ---------------------------------------------------------------------------
// The pipelines
// ---------------------------------------------------------------------------

interface PipelineCard {
  name: string
  what: string
  offices: string
  state: 'proven' | 'partial'
  note: string
}

const PIPELINES: readonly PipelineCard[] = [
  {
    name: 'Examination response',
    what:
      'The main one. An examiner has refused or objected; this answers the report. India’s FER is this same pipeline — it is not a separate thing to look for.',
    offices: 'USPTO · UKIPO · EUIPO · India TMR',
    state: 'proven',
    note: 'Run live against all four offices, with a real draft reviewed line by line for each.',
  },
  {
    name: 'Opposition counterstatement',
    what:
      'Somebody has opposed the application; this answers the notice allegation by allegation, and refuses to start if the opponent conflicts with a client already on your docket.',
    offices: 'India TMR verified · others coded',
    state: 'partial',
    note: 'Proven end to end on a real Indian notice. Evidence rounds, hearings and extensions are out of scope.',
  },
  {
    name: 'Specification amendment',
    what:
      'Narrowing the goods and services to get around an objection, with proposed wording drawn from the official classification.',
    offices: 'EUIPO exercised',
    state: 'partial',
    note: 'Built and driven to the final step, but no live amendment draft has ever been produced.',
  },
]

// ---------------------------------------------------------------------------
// What stays yours
// ---------------------------------------------------------------------------

const YOUR_JOB: readonly { title: string; detail: string }[] = [
  {
    title: 'Confirm the facts before the run',
    detail:
      'The office, the grounds and the mark are read off the paperwork and can be read wrong. Nothing is computed until you say they are right, and everything downstream inherits that answer.',
  },
  {
    title: 'Check the deadline against the papers yourself',
    detail:
      'Two dates are compared — the one printed on the letter and the one the office’s rules produce. Agreement is not confirmation. Only an attorney’s signature makes a date usable, and nothing is drafted against an unconfirmed one.',
  },
  {
    title: 'Supply what only the client knows',
    detail:
      'Prior use, reputation, acquired distinctiveness, honest concurrent use. These are never invented — an unsupported claim is left behind an [ATTORNEY: …] marker instead, and the marker is yours to answer or remove.',
  },
  {
    title: 'Read the draft, properly',
    detail:
      'Every citation is checked to exist and to say what the draft claims. Whether the argument is the right one for this client is a judgement no rule makes.',
  },
  {
    title: 'File it',
    detail:
      'Nothing here is sent to any registry, ever. Export produces a document; every step after that is yours.',
  },
]

/** Two habits found reviewing six real drafts. Worth naming, because they recur. */
const READ_FOR: readonly { title: string; detail: string }[] = [
  {
    title: 'An overstated holding',
    detail:
      'A case can be real, correctly cited, and leaned on for a broader rule than it actually established. Found once in six drafts.',
  },
  {
    title: 'A repeated sentence',
    detail:
      'Restating a rule near-verbatim a paragraph later is a habit of the drafting model — present in five of six drafts reviewed. Expect it on the next one.',
  },
  {
    title: 'A missing signature block',
    detail:
      'Four of six drafts ended with no valediction or signature block at all, not even a marker where one should be. This is an open defect, not a style choice.',
  },
]

// ---------------------------------------------------------------------------
// Honest limits
// ---------------------------------------------------------------------------

const LIMITS: readonly { title: string; detail: string }[] = [
  {
    title: 'A declined matter cannot be reopened',
    detail:
      'A document that comes back unreadable or not in English closes the matter permanently — on a free checks-only run exactly as readily as on a paid one. Paste the whole document, not an extract.',
  },
  {
    title: 'The UK register lookup has never fetched a real page',
    detail:
      'USPTO, EUIPO and WIPO lookups are proven live. The UKIPO route goes through a browser and has only ever been tested against constructed pages. Try one number and read what comes back before trusting it.',
  },
  {
    title: 'OCR has not met a genuinely poor scan',
    detail:
      'The mechanism works end to end on a clean image-only PDF. A crooked, low-resolution or stained scan is a materially harder problem and has not been tried.',
  },
  {
    title: 'Ranked search may be unavailable',
    detail:
      'Where the ranking model is not installed, search falls back to ordering by recorded outcome and says so on screen rather than pretending it ranked.',
  },
  {
    title: 'The offer email cannot be produced for a declined matter',
    detail:
      'Even where one is genuinely still due — a non-English notice, where the client letter would be in English anyway — the route refuses it. Write that one by hand.',
  },
  {
    title: 'Nothing here has been load tested',
    detail:
      'Every figure quoted anywhere in this product comes from matters run by hand, not from a load or concurrency test.',
  },
]

// ---------------------------------------------------------------------------
// The two research tools
// ---------------------------------------------------------------------------

interface ResearchTool {
  name: string
  what: string
  /** Why it is worth opening on a day with no matter to answer. */
  everyday: readonly string[]
  steps: readonly string[]
  honest: string
}

/**
 * The law library and the classification, described as research rather than as
 * plumbing.
 *
 * Both are reachable with no matter open and neither costs anything to read, so
 * they are the part of this platform an attorney can get value from on a day
 * they are not answering an office action at all. Nothing else on this page
 * makes that point, and the Intelligence tab does not make it either — it opens
 * on a table and lets you work out why you would want one.
 */
const RESEARCH_TOOLS: readonly ResearchTool[] = [
  {
    name: 'Nice class explorer',
    what:
      'The complete official classification — all 45 classes and every accepted description in them — searchable both ways: from a class to what belongs in it, and from a description of the goods to the classes that fit.',
    everyday: [
      'Choosing classes for a new application, before any objection exists.',
      'Checking whether wording a client has proposed is actually accepted in the class they want it in.',
      'Narrowing a specification to answer an objection, with wording taken from the official list rather than invented.',
      'Settling, in about a minute, which class an unusual product actually falls in.',
    ],
    steps: [
      'Open Intelligence, then the Nice classes tab.',
      'Type a class number to read that class whole, or search a word to find every description containing it.',
      'To go the other way, describe the goods or services in plain words under “Suggest classes” and press Suggest.',
      'Each suggestion comes back with the class, the official description it matched, and why it was chosen.',
    ],
    honest:
      'Suggest ranks against real descriptions and shows its reasoning for each. Any suggestion naming a description that was never retrieved is discarded, and the number discarded is shown — it cannot invent a class. Where no ranking model is configured it says so plainly and spends nothing.',
  },
  {
    name: 'Law explorer',
    what:
      'The provisions, decided cases, argument patterns and settled phrasing for all four offices, filtered by office and by the ground you are dealing with.',
    everyday: [
      'Reading the exact words of a provision before advising — no matter needs to exist.',
      'Seeing how a ground has actually gone at that office: which arguments succeeded, which failed, and what became of the application.',
      'Finding decisions analogous to the point you are about to argue, for a hearing or an opinion.',
      'Checking a phrase or an authority before you put it in a letter of your own.',
    ],
    steps: [
      'Open Intelligence. It opens on the law library.',
      'Pick an office, then a ground, to narrow everything below to what applies.',
      'Switch dataset with the row of tabs — Law for provisions, Decisions for outcomes, Authorities and Arguments for the material behind a draft.',
      'Click any citation to open it whole in the reading pane: chunk by chunk, with page numbers and the source it came from.',
      'On Decisions, filter by whether the ground succeeded or failed, and by what happened to the application.',
    ],
    honest:
      'Where part of a provision has no text in the library, the reading pane says how much is missing and tells you to read it against the office’s own published text. A chunk a grounds filter would otherwise hide is still shown, marked as withheld — so a filtered read never looks complete when it is not.',
  },
]

// ---------------------------------------------------------------------------
// Where things live
// ---------------------------------------------------------------------------

const WHERE: readonly { tab: TabKey; label: string; detail: string }[] = [
  { tab: 'new', label: 'New case', detail: 'Start one: paste, upload, link, or look up a number.' },
  { tab: 'opposition', label: 'Opposition', detail: 'The separate door for a notice of opposition, with its conflict check.' },
  { tab: 'queue', label: 'Queue', detail: 'Where you land after signing in: everything open, ordered by how close its confirmed deadline is.' },
  { tab: 'case', label: 'Case', detail: 'One matter: what to do next, the draft, and the full record of every run. The tab goes back to the matter you last had open in this browser tab.' },
  { tab: 'workbench', label: 'Workbench', detail: 'Work one signed run: clear the list, sign it off, export the letter. Like Case, it goes back to the matter you last had open.' },
  { tab: 'intelligence', label: 'Intelligence', detail: 'The law library, a research question, outcome rates, and every source a matter cited.' },
  { tab: 'courses', label: 'Courses', detail: 'Short recorded lessons on using the platform, with captions and a searchable transcript. Start with the first one.' },
]

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function SectionCard({
  kicker,
  title,
  description,
  children,
}: {
  kicker?: string
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <Card role="group" aria-label={title} className="gap-4">
      <CardHeader kicker={kicker} title={title} description={description} />
      {children}
    </Card>
  )
}

/** The five-step journey, with the attorney's own steps marked. */
function JourneyRail() {
  return (
    <ol className="m-0 grid list-none gap-3 p-0 md:grid-cols-5">
      {JOURNEY.map((step, index) => (
        <li
          key={step.title}
          className={cn(
            'flex flex-col gap-1.5 rounded-lg border p-3',
            step.yours ? 'border-brand-200 bg-brand-100' : 'border-divider bg-ground',
          )}
        >
          <div className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-micro font-semibold',
                step.yours ? 'bg-brand text-surface' : 'bg-divider text-ink',
              )}
            >
              {index + 1}
            </span>
            {step.yours ? (
              <Badge tone="accent">yours</Badge>
            ) : (
              <span className="text-micro text-muted">automatic</span>
            )}
          </div>
          <span className="text-meta font-semibold text-ink">{step.title}</span>
          <span className="text-micro text-muted">{step.detail}</span>
        </li>
      ))}
    </ol>
  )
}

/** The six steps inside a run, as a vertical rail with what stops each. */
function StageRail() {
  return (
    <ol className="m-0 flex list-none flex-col p-0">
      {STAGES.map((stage, index) => (
        <li key={stage.step} className="flex gap-3">
          {/* The spine: a dot per step, a line between them. */}
          <div aria-hidden="true" className="flex flex-col items-center">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-divider bg-surface text-micro font-semibold text-brand-700">
              {index + 1}
            </span>
            {index < STAGES.length - 1 ? <span className="w-px flex-1 bg-divider" /> : null}
          </div>

          <div className={cn('flex flex-col gap-1', index < STAGES.length - 1 && 'pb-4')}>
            <span className="text-meta font-semibold text-ink">{stage.step}</span>
            {/* The same step, under the name the Case screen's Stages table and
                Timeline print for it, so a reader can find it there. */}
            <span className="text-micro text-muted">
              Shown on the Case screen as “{stageLabel(stage.stage)}”
            </span>
            <span className="text-micro text-muted">{stage.plain}</span>
            {stage.stops ? (
              <span className="mt-0.5 rounded-md border border-block-line bg-block-bg px-2 py-1 text-micro text-block-fg">
                Can stop here — {stage.stops}
              </span>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  )
}

/** What the two vocabularies mean, so a badge on another screen reads. */
function Vocabulary() {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <div className="flex flex-col gap-2">
        <h5 className="m-0 text-meta">What a check can say about a draft</h5>
        <dl className="m-0 flex flex-col gap-1.5">
          {(
            [
              ['block', 'BLOCK', 'Refuses export outright. Not a judgement — a rule.'],
              ['warn', 'WARN', 'Worth your attention; does not stop anything.'],
              ['neutral', 'NOTE', 'An observation, recorded so it is not lost.'],
            ] as const
          ).map(([tone, code, meaning]) => (
            <div key={code} className="flex items-baseline gap-2">
              <dt className="shrink-0">
                <Badge tone={tone}>{code}</Badge>
              </dt>
              <dd className="m-0 text-micro text-muted">{meaning}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="flex flex-col gap-2">
        <h5 className="m-0 text-meta">What the review list asks of you</h5>
        <dl className="m-0 flex flex-col gap-1.5">
          {(
            [
              ['block', 'STOP', 'Must be resolved before anything can be sent.'],
              ['warn', 'VERIFY', 'Check it against a source. It may well be right.'],
              ['accent', 'SUPPLY', 'Only your client has this. No amount of drafting substitutes.'],
              ['outline', 'CONSIDER', 'A judgement call. Sendable without acting on it.'],
            ] as const
          ).map(([tone, code, meaning]) => (
            <div key={code} className="flex items-baseline gap-2">
              <dt className="shrink-0">
                <Badge tone={tone}>{code}</Badge>
              </dt>
              <dd className="m-0 text-micro text-muted">{meaning}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  )
}

/** One research tool: what it is, what it saves you, and the clicks. */
function ToolGuide({ tool }: { tool: ResearchTool }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-divider bg-ground p-4">
      <div className="flex flex-col gap-1">
        <h5 className="m-0 text-[15px]">{tool.name}</h5>
        <p className="m-0 text-micro text-muted">{tool.what}</p>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="card-kicker">Worth opening for</span>
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {tool.everyday.map((use) => (
            <li key={use} className="flex gap-2 text-micro text-ink">
              <span aria-hidden="true" className="text-brand-700">
                •
              </span>
              {use}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className="card-kicker">How to use it</span>
        <ol className="m-0 flex list-none flex-col gap-1 p-0">
          {tool.steps.map((step, index) => (
            <li key={step} className="flex gap-2 text-micro text-ink">
              <span
                aria-hidden="true"
                className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-divider text-[10px] font-semibold text-ink"
              >
                {index + 1}
              </span>
              {step}
            </li>
          ))}
        </ol>
      </div>

      <p className="m-0 rounded-md border border-warn-line bg-warn-bg px-2 py-1.5 text-micro text-warn-fg">
        {tool.honest}
      </p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// The screen
// ---------------------------------------------------------------------------

export function GuideScreen() {
  const router = useRouter()

  return (
    <div className="flex flex-col gap-6">
      <ScreenHeader
        kicker="Guide"
        title="How this works, and what stays yours"
        description="A trade mark response drafted here is a first draft with its sources checked — not advice, and never a filing. This page says exactly which parts are automatic, which are your decision, and where the platform is known to be weak."
      />

      <SectionCard
        kicker="In one paragraph"
        title="What the platform actually does"
      >
        <p className="m-0 max-w-[75ch] text-body text-ink">
          You give it an office action. It reads the document, works out which office and which
          grounds are in play, finds the response deadline two independent ways and refuses to
          proceed if they disagree, pulls the relevant law and argument patterns from a library of
          decided cases, and writes a draft response. Deterministic rules then check that draft —
          every citation resolves to a real source, every ground raised is answered, and nothing is
          asserted as fact that nobody supplied. Anything those rules refuse is listed for you, and
          the draft cannot be exported until it is cleared.
        </p>
        <p className="m-0 max-w-[75ch] text-body text-muted">
          What it does not do is decide anything that needs an attorney. It will not confirm a
          deadline, invent a fact about your client, judge whether an argument is the right one to
          run, or send anything to a registry.
        </p>
      </SectionCard>

      <SectionCard
        kicker="End to end"
        title="The five steps of a matter"
        description="Two of them are yours, and nothing moves past those on its own."
      >
        <JourneyRail />
      </SectionCard>

      <SectionCard
        kicker="Inside one run"
        title="What happens when you press run"
        description="In order. A run stops at the first step that refuses, and the steps below it never happen — which is why a stopped run has no draft rather than a bad one."
      >
        <StageRail />
        <p className="m-0 rounded-md bg-ground px-3 py-2 text-micro text-muted">
          Only the fifth step calls a model or costs anything. Turning drafting off runs every
          check for free — but a document that stops the run at step one or two closes the matter
          just the same, so paste the whole thing either way.
        </p>
      </SectionCard>

      <SectionCard
        kicker="Pipelines"
        title="The three kinds of matter it handles"
        description="Each is a different document to answer, with its own deadline rules and its own checks."
      >
        <div className="grid gap-3 md:grid-cols-3">
          {PIPELINES.map((pipeline) => (
            <div
              key={pipeline.name}
              className="flex flex-col gap-2 rounded-lg border border-divider bg-ground p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <h5 className="m-0 text-meta">{pipeline.name}</h5>
                <Badge tone={pipeline.state === 'proven' ? 'ok' : 'warn'}>
                  {pipeline.state === 'proven' ? 'proven live' : 'partly proven'}
                </Badge>
              </div>
              <p className="m-0 text-micro text-muted">{pipeline.what}</p>
              <p className="m-0 text-micro font-medium text-ink">{pipeline.offices}</p>
              <p className="m-0 text-micro text-muted">{pipeline.note}</p>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard
        kicker="Your responsibility"
        title="What the platform will not do for you"
        description="None of these is a setting that can be turned off. They are the reason a draft from here is safe to work from."
      >
        <ol className="m-0 flex list-none flex-col gap-2 p-0">
          {YOUR_JOB.map((item, index) => (
            <li
              key={item.title}
              className="flex gap-3 rounded-lg border border-brand-200 bg-brand-100 p-3"
            >
              <span
                aria-hidden="true"
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand text-micro font-semibold text-surface"
              >
                {index + 1}
              </span>
              <div className="flex flex-col gap-0.5">
                <span className="text-meta font-semibold text-ink">{item.title}</span>
                <span className="text-micro text-muted">{item.detail}</span>
              </div>
            </li>
          ))}
        </ol>
      </SectionCard>

      <SectionCard
        kicker="Before you file"
        title="Read every draft for these three things"
        description="Found by reviewing six real drafts line by line. They are habits, not one-offs — expect them on the next draft too."
      >
        <div className="grid gap-3 md:grid-cols-3">
          {READ_FOR.map((item) => (
            <div
              key={item.title}
              className="flex flex-col gap-1 rounded-lg border border-warn-line bg-warn-bg p-3"
            >
              <span className="text-meta font-semibold text-warn-fg">{item.title}</span>
              <span className="text-micro text-warn-fg">{item.detail}</span>
            </div>
          ))}
        </div>
      </SectionCard>

      <SectionCard
        kicker="Vocabulary"
        title="What the labels on other screens mean"
        description="These appear on the Case and Workbench screens. They are set by rules, never by a model."
      >
        <Vocabulary />
      </SectionCard>

      <SectionCard
        kicker="Honest limits"
        title="Where this is known to be weak"
        description="Listed because finding out after filing is worse. None of these is hypothetical."
      >
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {LIMITS.map((limit) => (
            <li key={limit.title} className="flex flex-col gap-0.5 rounded-md border border-divider px-3 py-2">
              <span className="text-meta font-medium text-ink">{limit.title}</span>
              <span className="text-micro text-muted">{limit.detail}</span>
            </li>
          ))}
        </ul>
      </SectionCard>

      <SectionCard kicker="Where to go" title="What each tab is for">
        <ul className="m-0 grid list-none gap-2 p-0 md:grid-cols-2">
          {WHERE.map((entry) => (
            <li key={entry.tab}>
              <button
                type="button"
                onClick={() => router.navigate(`/${entry.tab}`)}
                className="flex w-full flex-col gap-0.5 rounded-md border border-divider bg-surface px-3 py-2 text-left hover:border-brand-200 hover:bg-brand-100"
              >
                <span className="text-meta font-medium text-brand-700">{entry.label}</span>
                <span className="text-micro text-muted">{entry.detail}</span>
              </button>
            </li>
          ))}
        </ul>
      </SectionCard>

      <SectionCard
        kicker="Research, on any day"
        title="Two tools you can use without opening a matter"
        description="Both live under Intelligence, both are free to read, and neither needs a case in progress. They are the part of this platform that earns its keep on a day nobody has sent you an office action."
      >
        <div className="grid gap-3 md:grid-cols-2">
          {RESEARCH_TOOLS.map((tool) => (
            <ToolGuide key={tool.name} tool={tool} />
          ))}
        </div>
        <p className="m-0 rounded-md bg-ground px-3 py-2 text-micro text-muted">
          Everything either tool shows you is a stored source with its own identifier, not a
          summary written on the spot. That is what makes them quotable: you can open the exact
          text a claim came from and read it yourself.
        </p>
      </SectionCard>
    </div>
  )
}
